import React, { useEffect, useMemo, useState } from 'react'
import _isEmpty from 'lodash/isEmpty'
import { useIntl, FormattedMessage } from 'react-intl'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'
import { stateEntityAdapter } from '@racepointenergy/swc/scs/features/host-states'
import type { HomeId } from '@racepointenergy/swc/scs/types'
import type { State } from '@racepointenergy/swc/scs/features/host-states'
import type { SortingRule } from 'react-table'

import { SpinnerCentered } from 'components/Loader'
import { CommonSearch, MobileSortBy } from 'components/Table'
import { getSortOptions } from 'components/Table/utils'
import { commonMessages } from 'app/i18n/messages'
import { StyledFadeInWrapper } from 'components/Layout/CommonLayout/styles'

import SysMonGuard, { GuardRouterProps } from '../../SysMonGuard'
import HomeSystemStatesTable from './HomeSystemStatesTable'
import { systemStatesMessages } from './messages'
import { STATES_HEADERS } from './consts'
import {
  HomeSystemStatesContainer,
  HomeSystemStatesInfo,
  HomeSystemStatesSearchWrapper,
  HomeSystemStatesTitle,
} from './styles'

const hostStateInitialState = stateEntityAdapter.getInitialState()
const stateSelectors = stateEntityAdapter.getSelectors()

interface HomeSystemStatesProps {
  homeId: HomeId
}

function HomeSystemStates({ homeId }: HomeSystemStatesProps) {
  const { formatMessage } = useIntl()
  const {
    data = hostStateInitialState,
    isError,
    isFetching,
    error,
  } = dealerMonitorStates.useQuery({ id: homeId })
  const states = stateSelectors.selectAll(data)
  const headers = useMemo(
    () => ({
      state: {
        id: STATES_HEADERS.STATE,
        label: formatMessage(systemStatesMessages.stateNameColumn),
        desc: false,
      },
      value: {
        id: STATES_HEADERS.VALUE,
        label: formatMessage(systemStatesMessages.stateValueColumn),
        desc: false,
      },
      type: {
        id: STATES_HEADERS.TYPE,
        label: formatMessage(systemStatesMessages.stateTypeColumn),
        desc: false,
      },
    }),
    [formatMessage]
  )

  const sortOptions = useMemo(() => getSortOptions(headers), [headers])

  const [sortBy, setSortBy] = useState<SortingRule<State>>(sortOptions[0])
  const [searchValue, setSearchValue] = useState<string>()

  useEffect(() => {
    if (isError) {
      console.log('Error getting system states: ', error)
    }
  }, [isError, error])

  return (
    <StyledFadeInWrapper>
      <HomeSystemStatesContainer>
        <HomeSystemStatesTitle>
          {formatMessage(systemStatesMessages.title)}
        </HomeSystemStatesTitle>
        <HomeSystemStatesSearchWrapper>
          <CommonSearch
            setSearchValue={setSearchValue}
            placeholder={formatMessage(systemStatesMessages.searchPlaceholder)}
          />
        </HomeSystemStatesSearchWrapper>
        <MobileSortBy<State>
          headers={headers}
          sortOptions={sortOptions}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />

        {isError ? (
          <HomeSystemStatesInfo>
            {formatMessage(commonMessages.commonError)}
          </HomeSystemStatesInfo>
        ) : (
          <>
            {isFetching && _isEmpty(states) && <SpinnerCentered />}
            {!isFetching && _isEmpty(states) && (
              <HomeSystemStatesInfo>
                {formatMessage(systemStatesMessages.noStatesFound)}
              </HomeSystemStatesInfo>
            )}
            {!isFetching && !_isEmpty(states) && (
              <HomeSystemStatesTable
                headers={headers}
                states={states}
                defaultSortBy={sortOptions[0]}
                sortBy={sortBy}
                setSortBy={setSortBy}
                searchValue={searchValue}
              />
            )}
          </>
        )}
      </HomeSystemStatesContainer>
    </StyledFadeInWrapper>
  )
}

const HomeServicesGuard = (props: GuardRouterProps) => {
  const { homeId } = props.match.params
  return (
    <SysMonGuard
      homeId={homeId}
      title={<FormattedMessage {...systemStatesMessages.title} />}
    >
      <HomeSystemStates homeId={homeId} />
    </SysMonGuard>
  )
}

export default HomeServicesGuard
